import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useT } from "../i18n";
import { useFmt } from "../i18n/fmt";

type CimaInfo = {
  nombre: string;
  nregistro: string;
  labtitular: string | null;
  labcomercializador: string | null;
  principios_activos: { nombre: string; cantidad: string; unidad: string }[];
  atcs: { codigo: string; nombre: string; nivel: number }[];
  forma_farmaceutica: string | null;
  vias_administracion: string[];
  dosis: string | null;
  cpresc: string | null;
  receta: boolean | null;
  comerc: boolean | null;
  generico: boolean;
  biosimilar: boolean;
  huerfano: boolean;
  triangulo: boolean;
  ema: boolean;
  psum: boolean;
  estado_aut: string | null;
  estado_susp: string | null;
  presentaciones: { cn: string; nombre: string; comerc: boolean | null; estado_aut: string | null; estado_susp: string | null }[];
  ficha_tecnica_url: string | null;
  prospecto_url: string | null;
  excipientes: string[];
  no_sustituible: string | null;
};
type Bifimed = { cn: string; active_ingredient: string | null; product_name: string | null; financiado: string | null; tipos: string[]; nomenclator: string | null };
type ChangeRow = { id: string; change_type: string; product_name: string | null; active_ingredient: string | null; old_value: string | null; new_value: string | null; detected_at: string | null; is_portfolio: boolean; is_competitor: boolean; relevance_score: number | null; laboratory: string | null; source_code: string | null; source_name: string | null; extra_data: Record<string, any> | null;
  historico?: boolean };

type Detail = {
  cn: string;
  is_portfolio: boolean;
  is_competitor: boolean;
  matched_competitor_ingredient: string | null;
  portfolio: { product_name: string; active_ingredient: string; therapeutic_area: string | null; laboratory: string | null; is_active: boolean } | null;
  cima: CimaInfo | null;
  bifimed: Bifimed | null;
  // Agrupación homogénea (R-05, §90 Fase 2A)
  ah: { code: string; name: string | null; precio_menor: number | null; pvl: number | null; pvp: number | null;
        members: number; previous_code: string | null; changed_at: string | null; updated_at: string | null } | null;
  // Protección y exclusividad (catálogo 3.9, PE-02/PE-03): siempre visible en la ficha
  exclusividad: {
    regimen: "estandar" | "huerfano" | "anterior_2005" | "generico" | "sin_datos";
    texto: string; eu_number: string | null; nombre_ue: string | null; primera_autorizacion_ue: string | null;
    hitos: { kind: string; hito_id: string; fecha: string; label: string; sujeto_confirmacion: boolean; nota: string | null }[];
  } | null;
  // CO-02 como atributo (catálogo 4.0): ninguna presentación del nº de registro comercializada
  sin_comercializar?: { desde: string; base: "autorizacion" | "cese"; meses: number } | null;
  // VI-12: de dónde sale el texto de la ficha técnica que se compara por secciones
  ficha_tecnica?: { comparacion: "html" | "html_original" | "pdf" | "pendiente" | "no_disponible" } | null;
  changes: ChangeRow[];
  changes_count: number;
};

function Tag({ text, bg, color }: { text: string; bg: string; color: string }) {
  // Cápsula tintada cálida (píldora).
  return <span style={{ background: bg, color, padding: "3px 11px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap" }}>{text}</span>;
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: "0.7rem", color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: "0.86rem", color: "var(--clr-text)", fontWeight: 500, fontFamily: mono ? "monospace" : undefined, wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

// Iconos de línea (Heroicons outline). stroke currentColor, 16px.
function Ico({ path }: { path: React.ReactNode }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: "var(--clr-text4)" }}>
      {path}
    </svg>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: "0.92rem", fontWeight: 700, letterSpacing: "-0.01em", color: "var(--clr-text2)", margin: "0 0 12px 0", display: "flex", alignItems: "center", gap: 8 }}>
        {icon}{title}
      </h3>
      <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border2)", borderRadius: 12, padding: 14 }}>
        {children}
      </div>
    </section>
  );
}

function FinancingBadge({ value, G }: { value: string | null; G: string }) {
  if (!value) return <span style={{ color: "var(--clr-text4)" }}>–</span>;
  const v = value.toLowerCase();
  let bg = "#e2e8f0", color = "#475569";
  if (v.startsWith("si") || v.startsWith("sí")) { bg = G + "33"; color = G; }
  else if (v.includes("no incluido")) { bg = "#94a3b833"; color = "#475569"; }
  else if (v.includes("excluido")) { bg = "#f8717133"; color = "#dc2626"; }
  else if (v.includes("no financiado")) { bg = "#fb923c33"; color = "#ea580c"; }
  else if (v.includes("determinada")) { bg = "#fbbf2433"; color = "#a16207"; }
  return <Tag text={value} bg={bg} color={color} />;
}

export default function ProductDetailModal({ cn, onClose }: { cn: string; onClose: () => void }) {
  const t = useT();
  const fmt = useFmt();
  const G = useAuthStore(s => s.user?.primary_color) || "#00843D";
  const orgName = useAuthStore(s => s.user?.organization_name) || "ITF";
  const CT_LABEL: Record<string, string> = {
    new_entry: t("productDetail.typeNewEntry"), price_change: t("productDetail.typePriceChange"),
    reimbursement_change: t("productDetail.typeReimbursementChange"), exclusion: t("productDetail.typeExclusion"),
    deduction: t("productDetail.typeDeduction"), warning: t("productDetail.typeWarning"),
    marketing_status: t("productDetail.typeMarketingStatus"),
    eu_registration: t("productDetail.typeEuRegistration"),
    exclusivity: t("productDetail.typeExclusivity"),
  };
  const CT_COLOR: Record<string, string> = {
    new_entry: "#60a5fa", price_change: "#f59e0b",
    reimbursement_change: G, exclusion: "#f87171",
    deduction: "#a78bfa", warning: "#fb923c",
    marketing_status: "#f472b6",
    eu_registration: "#38bdf8",
    exclusivity: "#a855f7",
  };
  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true); setError("");
    api.get<Detail>(`/products/${cn}/detail`)
      .then(setData)
      .catch(e => setError(String(e?.message || e)))
      .finally(() => setLoading(false));
  }, [cn]);

  // Cerrar con Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const cima = data?.cima;
  const bif  = data?.bifimed;

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(11,18,21,0.4)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
      display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, padding: "5vh 2vw", overflowY: "auto",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--clr-modal-solid)", color: "var(--clr-text)",
        width: "min(1080px, calc(100vw - 24px))", maxHeight: "90vh", borderRadius: 22, border: "1px solid var(--clr-border)", boxShadow: "0 24px 80px -24px rgba(0,0,0,0.45)",
        overflowY: "auto", display: "flex", flexDirection: "column", fontVariantNumeric: "tabular-nums",
      }}>
        {/* Header */}
        <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--clr-border2)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              {data?.is_portfolio && <Tag text={orgName} bg={G + "33"} color={G} />}
              {data?.is_competitor && <Tag text={t("productDetail.competitor")} bg="#f59e0b33" color="#f59e0b" />}
              {!data?.is_portfolio && !data?.is_competitor && <Tag text={t("productDetail.others")} bg="#94a3b833" color="#94a3b8" />}
            </div>
            <h2 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.3 }}>
              {cima?.nombre || data?.portfolio?.product_name || t("productDetail.productFallback", { cn })}
            </h2>
            <div style={{ marginTop: 4, fontSize: "0.78rem", color: "var(--clr-text3)" }}>
              CN <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{cn}</span>
              {cima?.nregistro && <> · {t("productDetail.registrationNo")} <span style={{ fontFamily: "monospace" }}>{cima.nregistro}</span></>}
            </div>
          </div>
          <button onClick={onClose} aria-label={t("productDetail.close")} style={{
            width: 30, height: 30, borderRadius: 15, flexShrink: 0,
            background: "var(--clr-modal-solid2)", border: "1px solid var(--clr-border2)", cursor: "pointer",
            color: "var(--clr-text3)", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 0,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24, overflowY: "auto" }}>
          {loading && <div style={{ textAlign: "center", padding: 40, color: "var(--clr-text4)" }}>{t("productDetail.loading")}</div>}
          {error && <div style={{ textAlign: "center", padding: 40, color: "#ef4444" }}>{error}</div>}

          {data && !loading && (
            <>
              {/* Identificación */}
              <Section title={t("productDetail.identificationSection")} icon={<Ico path={<><path d="M7.5 7.5h.01" /><path d="M3 6a2 2 0 0 1 2-2h5.6a2 2 0 0 1 1.4.6l7 7a2 2 0 0 1 0 2.8l-4.6 4.6a2 2 0 0 1-2.8 0l-7-7A2 2 0 0 1 3 10.6V6Z" /></>} />}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14 }}>
                  <Field label={t("productDetail.activeIngredient")} value={cima?.principios_activos.map(p => `${p.nombre}${p.cantidad ? ` ${p.cantidad}${p.unidad || ''}` : ''}`).join(", ")} />
                  <Field label={t("productDetail.holderLab")} value={cima?.labtitular || data.portfolio?.laboratory} />
                  <Field label={t("productDetail.marketerLab")} value={cima?.labcomercializador && cima.labcomercializador !== cima.labtitular ? cima.labcomercializador : null} />
                  <Field label={t("productDetail.pharmaceuticalForm")} value={cima?.forma_farmaceutica} />
                  <Field label={t("productDetail.administrationRoute")} value={cima?.vias_administracion.join(", ")} />
                  <Field label={t("productDetail.dose")} value={cima?.dosis} />
                  {data.portfolio?.therapeutic_area && <Field label={t("productDetail.therapeuticArea")} value={data.portfolio.therapeutic_area} />}
                </div>
              </Section>

              {/* Agrupación homogénea (R-05): dimensión por la que se compara precio */}
              {data.ah && (
                <Section title={t("productDetail.ahSection")} icon={<Ico path={<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>} />}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
                    <Field label={t("productDetail.ahCode")} value={`${data.ah.code}${data.ah.name ? ` · ${data.ah.name}` : ""}`} />
                    <Field label={t("productDetail.ahMembers")} value={t("productDetail.ahMembersValue", { n: data.ah.members })} />
                    <Field label={t("productDetail.ahLowestPrice")} value={data.ah.precio_menor != null ? `${data.ah.precio_menor.toFixed(2)} €` : null} />
                    <Field label={t("productDetail.ahOwnPrice")} value={data.ah.pvp != null ? `${data.ah.pvp.toFixed(2)} € PVP${data.ah.pvl != null ? ` · ${data.ah.pvl.toFixed(2)} € PVL` : ""}` : null} />
                    {data.ah.previous_code && <Field label={t("productDetail.ahPrevious")} value={`${data.ah.previous_code}${data.ah.changed_at ? ` (${data.ah.changed_at.slice(0, 10)})` : ""}`} />}
                  </div>
                </Section>
              )}

              {/* Protección y exclusividad (Carlos, 28 sep): en la ficha, SIEMPRE, sin
                  ocultar por antigüedad; dos hitos distintos (datos y mercado). */}
              {data.exclusividad && (
                <Section title={t("productDetail.exclSection")} icon={<Ico path={<><path d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3z" /></>} />}>
                  <div style={{ fontSize: "0.8rem", color: "var(--clr-text3)", marginBottom: 10, lineHeight: 1.5 }}>{data.exclusividad.texto}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: data.exclusividad.hitos.length ? 12 : 0 }}>
                    <Field label={t("productDetail.exclEuNumber")} value={data.exclusividad.eu_number} mono />
                    <Field label={t("productDetail.exclFirstAuth")} value={data.exclusividad.primera_autorizacion_ue
                      ? fmt.date(new Date(data.exclusividad.primera_autorizacion_ue), { timeZone: "UTC" }) : null} />
                  </div>
                  {data.exclusividad.hitos.map(h => {
                    const days = Math.round((new Date(h.fecha).getTime() - Date.now()) / 86400000);
                    return (
                      <div key={`${h.kind}-${h.fecha}`} style={{ padding: "8px 0", borderTop: "1px solid var(--clr-border2)" }}>
                        <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "0.86rem", fontWeight: 600 }}>{h.label}</span>
                          <span style={{ fontSize: "0.86rem", fontVariantNumeric: "tabular-nums" }}>{fmt.date(new Date(h.fecha), { timeZone: "UTC" })}</span>
                          <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>
                            {days < 0 ? t("productDetail.exclPast") : t("productDetail.exclDaysLeft", { n: days })}
                          </span>
                          {h.sujeto_confirmacion && (
                            <span style={{ fontSize: "0.66rem", fontWeight: 700, color: "#b45309", border: "1px solid #b4530955", borderRadius: 999, padding: "1px 7px" }}>
                              {t(h.kind === "mercado" ? "productDetail.exclSubject" : "productDetail.exclCalculated")}
                            </span>
                          )}
                        </div>
                        {h.nota && <div style={{ fontSize: "0.74rem", color: "var(--clr-text4)", marginTop: 2, lineHeight: 1.45 }}>{h.nota}</div>}
                      </div>
                    );
                  })}
                </Section>
              )}

              {/* ATC */}
              {cima?.atcs && cima.atcs.length > 0 && (
                <Section title={t("productDetail.atcSection")} icon={<Ico path={<><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></>} />}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {cima.atcs.map(a => (
                      <div key={a.codigo} style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                        <span style={{ fontFamily: "monospace", fontWeight: 700, color: G, minWidth: 80 }}>{a.codigo}</span>
                        <span style={{ fontSize: "0.85rem", color: "var(--clr-text2)" }}>{a.nombre}</span>
                        <span style={{ marginLeft: "auto", fontSize: "0.7rem", color: "var(--clr-text4)" }}>{t("productDetail.level", { n: a.nivel })}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Estado regulatorio */}
              <Section title={t("productDetail.regulatoryStatusSection")} icon={<Ico path={<><rect x="8" y="3" width="8" height="4" rx="1" /><path d="M9 5H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-3" /><path d="M9 12h6M9 16h4" /></>} />}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
                  <Field label={t("productDetail.marketed")} value={cima?.comerc === true ? t("productDetail.yes") : cima?.comerc === false ? t("productDetail.no") : null} />
                  <Field label={t("productDetail.authorizationDate")} value={cima?.estado_aut} />
                  {cima?.estado_susp && <Field label={t("productDetail.suspensionDate")} value={cima.estado_susp} />}
                  <Field label={t("productDetail.prescription")} value={cima?.cpresc || (cima?.receta ? t("productDetail.prescriptionRequired") : t("productDetail.prescriptionNotRequired"))} />
                  {cima?.no_sustituible && <Field label={t("productDetail.notSubstitutable")} value={cima.no_sustituible} />}
                  {data.sin_comercializar && (
                    <Field label={t("productDetail.noPresentation")} value={
                      <span style={{ color: data.sin_comercializar.meses >= 36 ? "#dc2626" : data.sin_comercializar.meses >= 30 ? "#d97706" : undefined }}>
                        {t(data.sin_comercializar.base === "cese" ? "productDetail.noPresentationCese" : "productDetail.noPresentationAut", {
                          date: fmt.date(new Date(data.sin_comercializar.desde), { timeZone: "UTC" }), n: data.sin_comercializar.meses })}
                      </span>} />
                  )}
                  {data.ficha_tecnica && (
                    <Field label={t("productDetail.smpcWatch")} value={{
                      html: t("productDetail.smpcHtml"), html_original: t("productDetail.smpcOriginal"),
                      pdf: t("productDetail.smpcPdf"), pendiente: t("productDetail.smpcPending"),
                      no_disponible: t("productDetail.smpcUnavailable"),
                    }[data.ficha_tecnica.comparacion]} />
                  )}
                </div>
                <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {cima?.generico && <Tag text={t("productDetail.generic")} bg="#60a5fa22" color="#3b82f6" />}
                  {cima?.biosimilar && <Tag text={t("productDetail.biosimilar")} bg="#a78bfa22" color="#7c3aed" />}
                  {cima?.huerfano && <Tag text={t("productDetail.orphan")} bg="#f59e0b22" color="#d97706" />}
                  {cima?.triangulo && <Tag text={t("productDetail.triangle")} bg="#f8717122" color="#dc2626" />}
                  {cima?.ema && <Tag text="EMA" bg="#94a3b822" color="#475569" />}
                  {cima?.psum && <Tag text={t("productDetail.supplyProblems")} bg="#fb923c22" color="#ea580c" />}
                </div>
              </Section>

              {/* Financiación SNS — Bifimed */}
              <Section title={t("productDetail.financingSection")} icon={<Ico path={<><rect x="2.5" y="8" width="19" height="8" rx="4" /><path d="M12 8v8" /></>} />}>
                {bif ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, alignItems: "center" }}>
                    <div>
                      <span style={{ fontSize: "0.7rem", color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{t("productDetail.financingStatus")}</span>
                      <div style={{ marginTop: 4 }}><FinancingBadge value={bif.financiado} G={G} /></div>
                    </div>
                    <Field label={t("productDetail.type")} value={bif.tipos.length ? bif.tipos.join(", ") : null} />
                    <Field label={t("productDetail.nomenclator")} value={bif.nomenclator} />
                  </div>
                ) : (
                  <span style={{ color: "var(--clr-text4)", fontSize: "0.85rem" }}>{t("productDetail.noBifimedData")}</span>
                )}
              </Section>

              {/* Presentaciones */}
              {cima?.presentaciones && cima.presentaciones.length > 0 && (
                <Section title={t("productDetail.presentationsSection", { n: cima.presentaciones.length })} icon={<Ico path={<><path d="M21 8v8a2 2 0 0 1-1 1.7l-7 4a2 2 0 0 1-2 0l-7-4A2 2 0 0 1 3 16V8a2 2 0 0 1 1-1.7l7-4a2 2 0 0 1 2 0l7 4A2 2 0 0 1 21 8Z" /><path d="m3.3 7 8.7 5 8.7-5M12 22V12" /></>} />}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {cima.presentaciones.map(p => (
                      <div key={p.cn} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", padding: "6px 0", borderBottom: "1px dashed var(--clr-border2)" }}>
                        <span style={{ fontFamily: "monospace", fontWeight: 700, color: G }}>{p.cn}</span>
                        <span style={{ fontSize: "0.85rem", flex: 1, minWidth: 200 }}>{p.nombre}</span>
                        {p.comerc === true ? <Tag text={t("productDetail.marketed")} bg="#22c55e22" color="#16a34a" /> : p.comerc === false ? <Tag text={t("productDetail.notMarketed")} bg="#f8717122" color="#dc2626" /> : null}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Documentos */}
              {(cima?.ficha_tecnica_url || cima?.prospecto_url) && (
                <Section title={t("productDetail.documentsSection")} icon={<Ico path={<><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6Z" /><path d="M14 3v6h6" /></>} />}>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {cima.ficha_tecnica_url && <a href={cima.ficha_tecnica_url} target="_blank" rel="noreferrer" style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", color: "var(--clr-text)", padding: "6px 14px", borderRadius: 999, fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 7 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--clr-text4)" }}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6Z" /><path d="M14 3v6h6M9 13h6M9 17h6" /></svg>
                      {t("productDetail.datasheet")}</a>}
                    {cima.prospecto_url && <a href={cima.prospecto_url} target="_blank" rel="noreferrer" style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", color: "var(--clr-text)", padding: "6px 14px", borderRadius: 999, fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 7 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--clr-text4)" }}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" /></svg>
                      {t("productDetail.leaflet")}</a>}
                  </div>
                </Section>
              )}

              {/* Excipientes (importantes para alergias / formulación) */}
              {cima?.excipientes && cima.excipientes.length > 0 && (
                <Section title={t("productDetail.excipientsSection", { n: cima.excipientes.length })} icon={<Ico path={<><path d="M9 3h6M10 3v6.5L5.6 17a2 2 0 0 0 1.7 3h9.4a2 2 0 0 0 1.7-3L14 9.5V3" /><path d="M7.5 14h9" /></>} />}>
                  <div style={{ fontSize: "0.82rem", color: "var(--clr-text2)", lineHeight: 1.6 }}>
                    {cima.excipientes.join(" · ")}
                  </div>
                </Section>
              )}

              {/* Histórico de cambios detectados */}
              <Section title={t("productDetail.historySection", { n: data.changes_count })} icon={<Ico path={<><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-4 4" /></>} />}>
                {data.changes.length === 0 ? (
                  <span style={{ color: "var(--clr-text4)", fontSize: "0.85rem" }}>{t("productDetail.noChanges")}</span>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
                    {data.changes.map(c => {
                      const col = CT_COLOR[c.change_type] || "#94a3b8";
                      const stripText = (v: string | null) => {
                        if (!v) return null;
                        const sep = v.indexOf(" — ");
                        return sep !== -1 ? v.slice(0, sep) : v;
                      };
                      const newClean = stripText(c.new_value);
                      const showValue = (c.old_value || (newClean && newClean.length > 0));

                      // Etiqueta del concepto de precio según fuente
                      const priceConceptLabel: Record<string, string> = {
                        snsfarmacia: t("productDetail.conceptGenericFinanced"),
                        sns_pmah:    t("productDetail.conceptLowerPriceAH"),
                        sns_deducciones: t("productDetail.conceptMandatoryDeduction"),
                        cima:        t("productDetail.conceptAuthorizationDate"),
                        aemps_alerts: t("productDetail.conceptSafetyNote"),
                        boe:         t("productDetail.conceptBoeResolution"),
                      };
                      const concept = c.source_code ? priceConceptLabel[c.source_code] : null;

                      return (
                        <div key={c.id} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 12px", background: "var(--clr-surface)", border: "1px solid var(--clr-border2)", borderRadius: 12 }}>
                          <span style={{ fontSize: "0.7rem", color: "var(--clr-text4)", minWidth: 80, fontVariantNumeric: "tabular-nums", paddingTop: 1 }}>
                            {c.detected_at ? fmt.date(new Date(c.detected_at), { timeZone: "UTC" }) : "–"}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                                <span style={{ width: 9, height: 9, borderRadius: 5, background: col, display: "inline-block", flexShrink: 0, boxShadow: `0 0 0 3px ${col}22` }} />
                                <span style={{ fontSize: "0.78rem", fontWeight: 600, color: col }}>{CT_LABEL[c.change_type] || c.change_type}</span>
                              </span>
                              {c.source_name && (
                                <span style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", color: "var(--clr-text3)", padding: "1px 7px", borderRadius: 4, fontSize: "0.66rem", fontWeight: 600 }}>
                                  {c.source_name}
                                </span>
                              )}
                              {/* Historia anterior a la vigilancia (28 sep): solo aquí se ve. */}
                              {c.historico && (
                                <span title={t("hitos.historicoHint")} style={{ color: "var(--clr-text4)", border: "1px dashed var(--clr-border)", padding: "1px 7px", borderRadius: 4, fontSize: "0.64rem", fontWeight: 600 }}>
                                  {t("hitos.historico")}
                                </span>
                              )}
                            </div>
                            {concept && (
                              <div style={{ fontSize: "0.68rem", color: "var(--clr-text4)", marginTop: 2, fontStyle: "italic" }}>
                                {concept}
                              </div>
                            )}
                            {showValue && (
                              <div style={{ fontSize: "0.78rem", color: "var(--clr-text2)", marginTop: 2 }}>
                                {stripText(c.old_value) && <><span style={{ color: "var(--clr-text4)", textDecoration: "line-through" }}>{stripText(c.old_value)}</span> → </>}
                                <span style={{ color: newClean ? "var(--clr-text)" : "var(--clr-text4)", fontWeight: newClean ? 600 : 400 }}>{newClean || "–"}</span>
                              </div>
                            )}
                            {c.extra_data?.ai_summary && (
                              <div style={{ marginTop: 8, padding: "9px 12px", fontSize: "0.76rem",
                                color: "var(--clr-text2)", background: "var(--clr-hover)",
                                border: "1px solid var(--clr-border2)", borderRadius: 12,
                                lineHeight: 1.55, whiteSpace: "pre-line" }}>
                                <span style={{ fontWeight: 700, color: "var(--clr-text4)", fontSize: "0.62rem",
                                  textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 3 }}>
                                  {t("productDetail.aiSummary")}
                                </span>
                                {c.extra_data.ai_summary}
                              </div>
                            )}
                            {c.extra_data?.url && (
                              <a href={c.extra_data.url} target="_blank" rel="noreferrer"
                                style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 6, fontSize: "0.72rem",
                                  color: G, textDecoration: "none", fontWeight: 600 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6Z" /><path d="M14 3v6h6" /></svg>
                                {t("productDetail.viewAempsNote")}
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
